import { useEffect, useMemo, useState } from "react";
import { Form, Input, message } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import CustomModal from "components/Common/CustomModal";
import CommonSelect from "components/Common/CommonSelect";
import useReference from "hooks/useReference";
import type { Leave } from "types/leaveTypes";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { formatToSQLDate } from "utils/dateUtils";
import useLeave from "../hooks/useLeave";

interface AddEditLeaveProps {
  isOpen: boolean;
  onClose: () => void;
  leaveData?: Leave | null;
  onSave: (payload: any) => Promise<void> | void;
}



const AddEditLeave: React.FC<AddEditLeaveProps> = ({ isOpen, onClose, leaveData, onSave }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { periodLeaveOptions } = useLeave();

  const { leaveTypes, fetchLeaveTypes } = useReference();

  useEffect(() => {
    if (!isOpen) return;
    fetchLeaveTypes?.();
  }, [isOpen]);


  const leaveTypeOptions = useMemo(
    () => (leaveTypes || []).map((lt: any) => ({ value: lt.id, label: t(`leave.types.${lt.slug}`) || lt.name })),
    [leaveTypes]
  );
  const defaultLeaveTypeOption = useMemo(
    () => leaveTypeOptions.find((o: any) => o.value === leaveData?.leaveTypeId),
    [leaveTypeOptions, leaveData]
  );

  const defaultTypeOption = useMemo(
    () => periodLeaveOptions.find((o) => o.value === leaveData?.period),
    [leaveData]
  );

  useEffect(() => {
    form.resetFields();
    if (!isOpen) return;
    if (leaveData) {
      form.setFieldsValue({
        leaveTypeId: leaveData.leaveTypeId ?? undefined,
        period: leaveData.period ?? undefined,
        comment: leaveData.comment ?? undefined,
        startDate: leaveData.startDate ? dayjs(leaveData.startDate) : undefined,
        endDate: leaveData.endDate ? dayjs(leaveData.endDate) : undefined,
      });
    }

  }, [isOpen, leaveData, form]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const payload = {
        leaveTypeId: values.leaveTypeId,
        period: values.period,
        comment: values.comment ?? null,
        startDate: values.startDate ? formatToSQLDate(values.startDate) : undefined,
        endDate: values.endDate ? formatToSQLDate(values.endDate) : undefined,
      };
      await onSave(payload);
    }
    catch (error) {
      message.error(t("errors.VALIDATION_ERROR"));
      return;
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal
      title={leaveData?.id ? t("leave.edit") : t("leave.add")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={leaveData?.id ? t("save") : t("add")}
    >
      <Form form={form} layout="vertical">

        <Form.Item
          label={t("leave.form.leaveType")}
          name="leaveTypeId"
          rules={[{ required: true, message: t("leave.validation.please_select_leave_type") }]}
        >
          <CommonSelect
            options={leaveTypeOptions}
            defaultValue={defaultLeaveTypeOption}
            onChange={(option: any) => form.setFieldsValue({ leaveTypeId: option?.value })}
          />
        </Form.Item>

        <Form.Item
          label={t("leave.form.period")}
          name="period"
          rules={[{ required: true, message: t("leave.validation.please_select_period") }]}
        >
          <CommonSelect
            options={periodLeaveOptions}
            defaultValue={defaultTypeOption}
            onChange={(option: any) => form.setFieldsValue({ period: option?.value })}
          />
        </Form.Item>

        <div className="row">
          <div className="col-md-6">
            <Form.Item
              label={t("leave.form.startDate")}
              name="startDate"
              rules={[{ required: true, message: t("leave.validation.please_select_start_date") },]}
            >
              <LocalizedDatePicker className="w-100" placeholder={t("leave.form.startDate")} />
            </Form.Item>
          </div>
          <div className="col-md-6">
            <Form.Item
              label={t("leave.form.endDate")}
              name="endDate"
              rules={[{ required: true, message: t("leave.validation.please_select_end_date") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || !getFieldValue("startDate")) {
                    return Promise.resolve();
                  }
                  if (value < getFieldValue("startDate")) {
                    return Promise.reject(new Error(t("leave.validation.endDate_must_be_after_start_date")));
                  }
                  return Promise.resolve();
                },
              }),
              ]}
            >
              <LocalizedDatePicker className="w-100" placeholder={t("leave.form.endDate")} />
            </Form.Item>
          </div>
        </div>

        <Form.Item label={t("leave.form.comment")} name="comment">
          <Input.TextArea maxLength={1000} rows={3} placeholder={t("leave.form.comment")} />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default AddEditLeave;
